<template>
  <div class="content">
    <v-card class="pa-4">
      <v-card-title>Exclusions Ségur V2</v-card-title>
      <v-card-text>
        <p>
          Ce volet arrête la fenêtre d'enrôlement Ségur qui s'affiche sur le
          poste MediStory d'un médecin, quelle que soit la raison (médecin non
          concerné, exerçant à l'étranger, ne souhaitant pas s'enrôler, etc.).
          Saisir le RPPS du médecin et un motif. L'arrêt prend effet au prochain
          rafraîchissement de son poste (sous environ 1 heure) et reste
          réversible (bouton « Réactiver les relances V2 »).
        </p>
        <p>
          Un médecin ayant déjà signé un bon de commande Ségur V2 ne peut pas
          être exclu : ses étapes de signature restent dues.
        </p>

        <v-row class="align-center">
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="rppsInput"
              label="RPPS du médecin (11 chiffres)"
              outlined
              dense
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="reasonInput"
              label="Motif (obligatoire)"
              placeholder="ex. Suisse - hors Ségur"
              outlined
              dense
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3" class="d-flex align-center">
            <v-btn
              color="#e2007a"
              dark
              :loading="submitting"
              :disabled="!canSubmit"
              @click="excludeDoctor"
            >
              Exclure ce médecin
            </v-btn>
          </v-col>
        </v-row>

        <v-alert
          v-if="feedback.message"
          :type="feedback.type"
          dense
          text
          class="mt-2"
        >
          {{ feedback.message }}
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card class="pa-4 mt-4">
      <v-card-title>Médecins exclus</v-card-title>
      <v-data-table
        :headers="headers"
        :items="rows"
        :options.sync="options"
        :server-items-length="count"
        :loading="loading"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        no-data-text="Aucun médecin exclu"
        @update:options="fetchExclusions"
      >
        <template v-slot:[`item.name`]="{ item }">
          {{ displayName(item) }}
        </template>
        <template v-slot:[`item.segurV2OptedOutAt`]="{ item }">
          {{ formatDate(item.segurV2OptedOutAt) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn small text color="primary" @click="reactivate(item)">
            Réactiver les relances V2
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "SegurExclusions",
  data() {
    return {
      rppsInput: "",
      reasonInput: "",
      submitting: false,
      feedback: { type: "info", message: "" },
      options: { page: 1, itemsPerPage: 50 },
      headers: [
        { text: "RPPS", value: "RPPS" },
        { text: "Médecin", value: "name", sortable: false },
        { text: "Date d'exclusion", value: "segurV2OptedOutAt" },
        { text: "Motif", value: "segurV2OptOutReason", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    loading() {
      return this.$store.state.segur.loading || false;
    },
    rows() {
      return this.$store.getters["segur/getSegurExclusions"]?.rows || [];
    },
    count() {
      return this.$store.getters["segur/getSegurExclusions"]?.count || 0;
    },
    canSubmit() {
      return (
        /^8?\d{11}$/.test(this.rppsInput.trim()) &&
        this.reasonInput.trim().length >= 2
      );
    },
  },
  methods: {
    // Appelé par v-data-table au montage et à chaque changement de page / taille.
    async fetchExclusions() {
      const { page, itemsPerPage } = this.options;
      try {
        await this.$store.dispatch("segur/getSegurExclusions", {
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
        });
      } catch (e) {
        this.setFeedback("error", this.errorMessage(e));
      }
    },
    async excludeDoctor() {
      this.submitting = true;
      try {
        await this.$store.dispatch("segur/setSegurOptOut", {
          rpps: this.rppsInput.trim(),
          optedOut: true,
          reason: this.reasonInput.trim(),
        });
        this.setFeedback("success", "Médecin exclu du Ségur V2.");
        this.rppsInput = "";
        this.reasonInput = "";
        await this.fetchExclusions();
      } catch (e) {
        this.setFeedback("error", this.errorMessage(e));
      } finally {
        this.submitting = false;
      }
    },
    async reactivate(item) {
      try {
        await this.$store.dispatch("segur/setSegurOptOut", {
          rpps: item.RPPS,
          optedOut: false,
        });
        this.setFeedback("success", "Relances V2 réactivées pour ce médecin.");
        await this.fetchExclusions();
      } catch (e) {
        this.setFeedback("error", this.errorMessage(e));
      }
    },
    displayName(item) {
      const nom = (item.INFOS_PORTEUR_NomPatronym || "").trim();
      const prenom = (item.INFOS_PORTEUR_PrenomUsuel || "").trim();
      const full = `${prenom} ${nom}`.trim();
      return full || "Nom inconnu";
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString("fr-FR");
    },
    setFeedback(type, message) {
      this.feedback = { type, message };
    },
    errorMessage(e) {
      return (
        e?.response?.data?.error ||
        e?.response?.data?.data?.error ||
        e?.message ||
        "Erreur inattendue"
      );
    },
  },
};
</script>

<style lang="scss" scoped></style>
